import {
  CalculatedTax,
  CancelSubscriptionArgs,
  ChangePlanSummaryDto,
  ChangeSubscriptionArgs,
  CreateSubscriptionFromTokenData,
  CreditCardTokenData,
  CrudSubscriptionResponseDto,
  GetInvoicesArgs,
  GetCouponArgs,
  GetPlanInfoArgs,
  InvoicesPageDto,
  PaymentDto, RetryPaymentArgs,
  UpdateTaxArgs
} from 'booksprout'
import { gql } from '@apollo/client/core'
import { useApollo } from '../apollo/apolloModule'
import { useConfig } from '../config/configModule'

/**
 * Quaderno's tax SDK. A script tag in each app's index template defines it, so it is real at runtime and
 * absent from the TS program — which is why every call below used to carry its own `@ts-ignore`. Declared
 * once instead: a suppression per call site hides any OTHER error on those lines too.
 */
declare const Quaderno: any

/**
 * Replaces `PaymentService`.
 *
 * Never a CRUD service — there is no payment REST controller and no `BaseCrudService` in its
 * ancestry — so it spreads nothing and just holds its own members. `stripeKey` and `cycles` stay
 * getters on the bag because consumers read them as properties, and `stripeKey` has to resolve
 * against config at read time: `SelectPlan` mounts before `useConfig().init()` has necessarily run
 * on a cold SSR request.
 */

/**
 * The billing cycle keys the UI switches on. Was `PaymentCycles` in payment.service.interface.ts,
 * which existed only as a DI token type.
 */
export interface PaymentCycles {
  year: string
  month: string
}

const paymentFragments = {
  planInfo: gql`
    fragment PlanInfoFragment on PaymentDto {
      stripeCustomerId,
      stripeSubscriptionId,
      plan,
      price,
      cycle,
      nextBillDue,
      cardLast4,
      cardExpiry,
      taxCountry,
      taxCountryCode,
      taxCode,
      postalCode,
      cardType,
      cancelAt,
      discountPcApplied,
      discountAmountApplied,
      lastFailedPaymentId
      invoices {
        id,
        description,
        paidOn,
        cardLast4,
        downloadLink,
        cardType,
        status,
        paymentFailedReason,
        paymentFailedDescription,
        paymentFailedType
      }
    }
  `
}

const getStripeKey = (): string => {
  return useConfig().config().stripeApiKey
}

const getCycles = (): PaymentCycles => {
  return {
    year: 'year',
    month: 'month'
  }
}

/**
 * Chained by the consumers (`globalLoader(false).getPlanInfo()`), so it hands back this bag.
 */
const globalLoader = (show: boolean) => {
  useApollo().globalLoader(show)
  return service
}

const initQuaderno = async (): Promise<never | void> => {
  if (typeof Quaderno === 'undefined') {
    // make sure we capture quadernoInitError so Sentry can pick it up
    console.error('quadernoInitError')
    return Promise.reject('quadernoInitError')
  }

  if (!Quaderno.initialized) {
      await Quaderno.init(useConfig().config().quadernoApiKey).then(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log('Quaderno.js successfully initialized')
      }
          Quaderno.initialized = true
    }).catch((error: any) => {
      console.log(error.description, error.messages)
      return Promise.reject(error.messages)
    })
  }
}

const calculateTaxes = async (amount: number, countryCode: string, postalCode: string, taxNumber: string): Promise<CalculatedTax> => {
  await initQuaderno()

  return Quaderno.calculateTaxes({
    country: countryCode,
    businessNumber: taxNumber,
    postalCode: postalCode
  }).then((taxObject: any) => {
      const calculatedPrice = Quaderno.calculatePrice({
      amount: amount * 100,
      quantity: 1,
      taxType: 'excluded'
    })

    return {
      rate: taxObject.rate,
      countryCode,
      name: taxObject.name,
      extraName: taxObject.extraName,
      extraRate: taxObject.extraRate,
      price: {
        total: calculatedPrice.total / 100,
        taxes: calculatedPrice.taxes / 100
      }
    }
  })
}

const isValidTaxNumber = async (taxNumber: string, countryCode: string): Promise<boolean> => {
  await initQuaderno()

  return Quaderno.validateBusinessNumber(taxNumber, countryCode.toUpperCase()).then((response) => {
    return response.valid
  }).catch((_error: any) => {
    return false
  })
}

const subscribe = async (data: CreateSubscriptionFromTokenData): Promise<CrudSubscriptionResponseDto> => {
  return useApollo().mutate({
    variables: {
      cardBrand: data.tokenData.card.brand,
      cardCountry: data.tokenData.card.country,
      cardId: data.tokenData.card.id,
      cardExpMonth: data.tokenData.card.expMonth,
      cardExpYear: data.tokenData.card.expYear,
      cardLast4: data.tokenData.card.last4,
      taxRate: data.tokenData.amount.rate,
      extraTaxRate: data.tokenData.amount.extraRate,
      subtotal: data.tokenData.amount.subtotal,
      taxCountryCode: data.tokenData.amount.taxCountryCode,
      taxAmount: data.tokenData.amount.taxes,
      total: data.tokenData.amount.total,
      token: data.tokenData.token,
      postalCode: data.tokenData.address.postalCode,
      plan: data.plan,
      planCycle: data.planCycle,
      taxCode: data.tokenData.taxCode,
      taxName: data.tokenData.taxName,
      extraTaxName: data.tokenData.extraTaxName,
      ipAddress: data.ipAddress
    },
    mutation: gql`
      mutation CreateSubscription (
        $cardBrand: String!
        $cardCountry: String!
        $cardId: String!
        $cardExpMonth: Int!
        $cardExpYear: Int!
        $cardLast4: String!
        $taxRate: Float!
        $extraTaxRate: Float
        $subtotal: Float!
        $taxCountryCode: String!
        $taxAmount: Float!
        $token: String!
        $total: Float!
        $postalCode: String!
        $plan: String!
        $planCycle: String!
        $taxCode: String!
        $taxName: String!
        $extraTaxName: String
        $ipAddress: String
      ) {
        createSubscription (
          cardBrand: $cardBrand
          cardCountry: $cardCountry
          cardId: $cardId
          cardExpMonth: $cardExpMonth
          cardExpYear: $cardExpYear
          cardLast4: $cardLast4
          taxRate: $taxRate
          extraTaxRate: $extraTaxRate
          subtotal: $subtotal
          taxCountryCode: $taxCountryCode
          taxAmount: $taxAmount
          token: $token
          total: $total
          postalCode: $postalCode
          plan: $plan
          planCycle: $planCycle
          taxCode: $taxCode,
          taxName: $taxName,
          extraTaxName: $extraTaxName,
          ipAddress: $ipAddress
        ) {
          actionUrl,
          actionUrlType,
          clientSecret,
          status
        }
      }
    `
  }).then((result: any) => {
    return result
  })
}

const getPlanInfo = async (args?: GetPlanInfoArgs): Promise<PaymentDto> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetPlanInfo (
        $forUserId: Int,
        $skipInvoices: Boolean
      ) {
        planInfo (
          skipInvoices: $skipInvoices,
          forUserId: $forUserId
        ) {
          ...PlanInfoFragment
        }
      }
      ${paymentFragments.planInfo}
    `
  })
}

const getInvoices = async (args?: GetInvoicesArgs): Promise<InvoicesPageDto> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetInvoices (
        $forUserId: Int,
        $limit: Int,
        $startingAfter: String
      ) {
        invoices (
          forUserId: $forUserId,
          limit: $limit,
          startingAfter: $startingAfter
        ) {
          invoices {
            id,
            description,
            paidOn,
            cardLast4,
            downloadLink,
            cardType,
            status,
            paymentFailedReason,
            paymentFailedDescription,
            paymentFailedType
          },
          hasMore,
          nextStartingAfter
        }
      }
    `
  })
}

const changeSubscriptionSummary = async (args: ChangeSubscriptionArgs): Promise<ChangePlanSummaryDto> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query ChangeSubscriptionSummary (
        $newPlan: String!
        $oldPlan: String!
        $newCycle: String!
        $proratedDate: Int
      ) {
        changeSubscriptionSummary (
          newPlan: $newPlan,
          newCycle: $newCycle,
          oldPlan: $oldPlan,
          proratedDate: $proratedDate
        ) {
          subtotal,
          tax,
          total,
          credit,
          referralCredit,
          amountDue,
          taxCountryCode,
          proratedDate,
          taxCode,
          postalCode
        }
      }
    `
  })
}

const hasPastDueInvoice = async (): Promise<boolean> => {
  return useApollo().query({
    query: gql`
      query HasPastDueInvoice {
        hasPastDueInvoice
      }
    `
  })
}

const changeSubscription = async (args: ChangeSubscriptionArgs): Promise<CrudSubscriptionResponseDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation changeSubscription (
        $newPlan: String!
        $oldPlan: String!
        $newCycle: String!
        $proratedDate: Int
        $ipAddress: String
      ) {
        changeSubscription (
          newPlan: $newPlan,
          newCycle: $newCycle,
          oldPlan: $oldPlan,
          proratedDate: $proratedDate,
          ipAddress: $ipAddress
        ) {
          actionUrl,
          actionUrlType,
          clientSecret,
          status
        }
      }
    `
  })
}

const cancelSubscription = async (args: CancelSubscriptionArgs): Promise<PaymentDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation CancelSubscription (
        $reason: Int,
        $reasonDescription: String
      ) {
        cancelSubscription (
          reason: $reason,
          reasonDescription: $reasonDescription
        ) {
          ...PlanInfoFragment
        }
      }
      ${paymentFragments.planInfo}
    `
  })
}

const removeCreditCard = async (): Promise<PaymentDto> => {
  return useApollo().mutate({
    mutation: gql`
      mutation RemoveCreditCard {
        removeCreditCard {
          ...PlanInfoFragment
        }
      }
      ${paymentFragments.planInfo}
    `
  })
}

const addCreditCard = (tokenData: CreditCardTokenData): Promise<PaymentDto> => {
  return useApollo().mutate({
    variables: {
      cardBrand: tokenData.card.brand,
      cardCountry: tokenData.card.country,
      cardId: tokenData.card.id,
      cardExpMonth: tokenData.card.expMonth,
      cardExpYear: tokenData.card.expYear,
      cardLast4: tokenData.card.last4,
      taxRate: 0,
      subtotal: 0,
      taxCountryCode: '',
      taxAmount: 0,
      total: 0,
      token: tokenData.token,
      postalCode: tokenData.address.postalCode,
      plan: '',
      planCycle: '',
      taxCode: '',
      taxName: ''
    },
    mutation: gql`
      mutation AddCreditCard (
        $cardBrand: String!
        $cardCountry: String!
        $cardId: String!
        $cardExpMonth: Int!
        $cardExpYear: Int!
        $cardLast4: String!
        $taxRate: Float!
        $subtotal: Float!
        $taxCountryCode: String!
        $taxAmount: Float!
        $token: String!
        $total: Float!
        $postalCode: String!
        $plan: String!
        $planCycle: String!
        $taxCode: String!
        $taxName: String!
      ) {
        addCreditCard (
          cardBrand: $cardBrand
          cardCountry: $cardCountry
          cardId: $cardId
          cardExpMonth: $cardExpMonth
          cardExpYear: $cardExpYear
          cardLast4: $cardLast4
          taxRate: $taxRate
          subtotal: $subtotal
          taxCountryCode: $taxCountryCode
          taxAmount: $taxAmount
          token: $token
          total: $total
          postalCode: $postalCode
          plan: $plan
          planCycle: $planCycle
          taxCode: $taxCode,
          taxName: $taxName
        ) {
          ...PlanInfoFragment
        }
      }
      ${paymentFragments.planInfo}
    `
  }).then((result: any) => {
    return result
  })
}

const updateTaxInformation = (args: UpdateTaxArgs): Promise<ChangePlanSummaryDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation CreateSubscription (
        $taxRate: Float!
        $extraTaxRate: Float
        $taxCountryCode: String!
        $postalCode: String!
        $taxCode: String!
        $taxName: String!
        $extraTaxName: String
      ) {
        updateTaxInfo (
          taxRate: $taxRate
          extraTaxRate: $extraTaxRate
          taxCountryCode: $taxCountryCode
          postalCode: $postalCode
          taxCode: $taxCode
          taxName: $taxName
          extraTaxName: $extraTaxName
        ) {
          subtotal,
          tax,
          total,
          credit,
          taxCountryCode,
          taxCode,
          postalCode
        }
      }
    `
  }).then((result: any) => {
    return result
  })
}

const reactivateCurrentPlan = (): Promise<PaymentDto> => {
  return useApollo().mutate({
    mutation: gql`
      mutation ReactivateCurrentPlan {
        reactivateCurrentPlan {
          ...PlanInfoFragment
        }
      }
      ${paymentFragments.planInfo}
    `
  }).then((result: any) => {
    return result
  })
}

const retryPayment = (args: RetryPaymentArgs): Promise<PaymentDto> => {
  return useApollo().mutate({
    variables: {
      ...args,
    },
    mutation: gql`
      mutation RetryPayment (
        $invoiceId: String!
      ) {
        retryPayment (
          invoiceId: $invoiceId
        ) {
          ...PlanInfoFragment
        }
      }
      ${paymentFragments.planInfo}
    `
  }).then((result: any) => {
    return result
  })
}

const getCouponDiscount = async (args: GetCouponArgs): Promise<number> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetCouponDiscount (
        $forFp: Boolean
        $cycle: String
        $plan: String
      ) {
        getCouponDiscount (
          forFp: $forFp,
          cycle: $cycle
          plan: $plan
        )
      }
    `
  }).then((result: any) => {
    return result
  })
}

const service = {
  get stripeKey () {
    return getStripeKey()
  },

  get cycles () {
    return getCycles()
  },

  initQuaderno,
  calculateTaxes,
  isValidTaxNumber,
  subscribe,
  getPlanInfo,
  getInvoices,
  changeSubscriptionSummary,
  hasPastDueInvoice,
  changeSubscription,
  cancelSubscription,
  removeCreditCard,
  addCreditCard,
  updateTaxInformation,
  reactivateCurrentPlan,
  retryPayment,
  getCouponDiscount,
  globalLoader
}

export function paymentService () {
  return service
}
